// Shared WebSocket contracts for Optimus frontend/backend.
// Keep this file dependency-free and in plain TypeScript.

// Timeframe enum (string union)
export enum Timeframe {
  "1m" = "1m",
  "5m" = "5m",
  "15m" = "15m",
  "30m" = "30m",
  "1h" = "1h",
  "4h" = "4h",
  "6h" = "6h",
  "1d" = "1d",
  "1w" = "1w",
  "1M" = "1M",
}

// Core DTOs
export type CandleDTO = {
  symbol: string;
  pair: string;
  exchange: string;
  timeframe: Timeframe;
  open: number;
  close: number;
  high: number;
  low: number;
  volume: number;
  timestamp: number;
};

export type LineDTO = {
  priceStart: number;
  priceEnd: number;
  timeStart: number;
  timeEnd: number;
};

// Shared contract for trade-oriented signals emitted by indicators.
export type TradeSignalAction = "BUY" | "SELL" | "LONG" | "SHORT";
export type TradeSignalEvent<TAction extends string = TradeSignalAction> = {
  symbol: string;
  pair: string;
  exchange: string;
  timeframe: Timeframe;
  timestamp: number;
  action: TAction;
};

// Event names for WS (client should subscribe to these)
export const EventWsTypes = {
  InitialData: "initialData",
  Error: "error",
  Candle: "WS_CANDLE_EVENT",
  Candles: "WS_CANDLES_EVENT",
  PlasmaLines: "WS_PLASMA_EVENT",
  SupportResistance: "WS_SUPPORT_RESISTANCE_EVENT",
  BreakoutCarver: "WS_BREAKOUT_CARVER_EVENT",
  TcBreakout: "WS_TC_BREAKOUT_EVENT",
  FearCarver: "WS_FEAR_CARVER_EVENT",
  VectorCandle: "WS_VECTOR_CANDLE_EVENT",
  TurtleSignal: "WS_TURTLE_SIGNAL_EVENT",
  TcTrend: "WS_TCTREND_EVENT",
  DeltaTrend: "WS_DELTATREND_EVENT",
  FearGreed: "WS_FEARGREED_EVENT",
  AuctionMarket: "WS_AUCTION_MARKET_EVENT",
} as const;
export type EventWsType = (typeof EventWsTypes)[keyof typeof EventWsTypes];
export type EventWsTypesMap = typeof EventWsTypes;

// WS payloads
export type WSCandlePayload = CandleDTO;
export type WSCandlesPayload = CandleDTO[];
export type WSPlasmaLinesPayload = LineDTO[];
export type WSSupportResistancePayload = {
  supportLines: LineDTO[];
  resistanceLines: LineDTO[];
};

export type BreakoutCarverAction = "LONG" | "SHORT";
export type BreakoutCarverEvent = TradeSignalEvent<BreakoutCarverAction> & {
  scaledPrice: number;
  volumeRatio?: number; // Current volume / average volume (confirmation strength)
};
export type WSBreakoutCarverPayload = BreakoutCarverEvent[];

export type TcBreakoutEvent = TradeSignalEvent<BreakoutCarverAction> & {
  trend?: TcTrendDirection;
};
export type WSTcBreakoutPayload = TcBreakoutEvent[];

export type FearCarverEvent = BreakoutCarverEvent & {
  sentiment: number;
  volumeRatio?: number; // Inherited from BreakoutCarver
};
export type WSFearCarverPayload = FearCarverEvent[];

export enum VectorCandleType {
  GRAY = "GRAY",
  RED = "RED",
  GREEN = "GREEN",
  BLUE = "BLUE",
  VIOLET = "VIOLET",
}
export type VectorCandleAction = "BUY" | "SELL" | "NONE";
export type VectorCandleEvent = TradeSignalEvent<VectorCandleAction> & {
  vectorType: VectorCandleType;
  speedRatio: number;
  recoveryTime: number;
  confidence: number;
  previousCandleSpeed: number;
  currentCandleSpeed: number;
  priceRecovery: number;
};
export type WSVectorCandlePayload = VectorCandleEvent[];

export type TurtleAction =
  | "enter_long"
  | "enter_short"
  | "add_long"
  | "add_short"
  | "exit_long"
  | "exit_short";
export type TurtleSignal = TradeSignalEvent<TurtleAction> & {
  price: number;
  stopPrice: number;
  units?: number;
  risk?: number;
};
export type WSTurtleSignalPayload = TurtleSignal[];

export enum TcTrendDirection {
  GOLD = "🟡",
  SILVER = "⚪",
  BLUE = "🔵",
  UNKNOWN = "?",
}
export type TcTrendSnapshot = {
  pair: string;
  exchange: string;
  timeframe: Timeframe;
  trend?: TcTrendDirection;
  trendPrevious?: TcTrendDirection;
  updatedAt: number;
};
// For TcTrend we send the change history for the current view
export type WSTcTrendPayload = TcTrendSnapshot[];

// DeltaTrend: trackline + dots + labels
export enum DeltaLabelKind {
  TOP = "TOP",
  BOTTOM = "BOTTOM",
}

export type DeltaDot = {
  symbol: string;
  pair: string;
  exchange: string;
  timeframe: Timeframe;
  timestamp: number;
  price: number; // y-coordinate for drawing (low for UP dot, high for DOWN dot)
  type: "UP" | "DOWN";
};

export type DeltaLabel = {
  symbol: string;
  pair: string;
  exchange: string;
  timeframe: Timeframe;
  timestamp: number;
  price: number; // y-coordinate where label should be drawn
  kind: DeltaLabelKind;
};

export type DeltaLinePoint = {
  timestamp: number;
  price: number;
};

export type WSDeltaTrendPayload = {
  dots: DeltaDot[];
  labels: DeltaLabel[];
  line: DeltaLinePoint[];
};

// Fear & Greed overlay
export type FearGreedPoint = {
  bucket: string;
  timestamp: number;
  value: number; // 0-100 scale
  classification?: string;
  scaledPrice?: number; // optional scaled value when overlaying on price axis
};

export type WSFearGreedPayload = {
  points: FearGreedPoint[];
  latest?: FearGreedPoint;
  updatedAt: number;
};

// Auction Market Theory overlay
export enum AmtMarketPhase {
  BALANCE = "BALANCE",
  IMBALANCE = "IMBALANCE",
}

export enum AmtLiquidityType {
  STOP_RUN = "STOP_RUN",
  LIQUIDITY_GRAB = "LIQUIDITY_GRAB",
  FLIP = "FLIP",
  MAGNET = "MAGNET",
  BARRIER = "BARRIER",
}

export type AmtAction = "BUY" | "SELL";

export type MarketTimeframe = "1d" | "1w" | "1M" | "3M" | "1Y";

export type AuctionMarketEvent = TradeSignalEvent<AmtAction> & {
  phase: AmtMarketPhase;
  liquidityType: AmtLiquidityType;
  confidence: number;
  marketTimeframe: MarketTimeframe;
  marketPeriodStart: number;
  marketPeriodEnd: number;
  valueAreaHigh: number;
  valueAreaLow: number;
  poc: number;
  volumeRatio: number;
  priceRecovery: number;
  isHighTimeframeTrendAligned: boolean;
};

export type WSAuctionMarketPayload = AuctionMarketEvent[];

// Indicator Settings (Admin)
export type IndicatorSettingDTO = {
  timeframe: Timeframe;
  enabled: boolean;
  config?: Record<string, unknown>;
};

export type IndicatorGroupDTO = {
  indicatorId: string;
  label: string;
  settings: IndicatorSettingDTO[];
};

export type IndicatorInfoDTO = {
  id: string;
  label: string;
};

export type UpdateSettingDTO = {
  enabled: boolean;
  config?: Record<string, unknown>;
};

export type BulkUpdateDTO = {
  settings: { timeframe: Timeframe; enabled: boolean }[];
};
